import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactAreas = [
  { name: 'Bendinat', link: '/bendinat' },
  { name: 'Sol de Mallorca', link: '/sol-de-mallorca' },
  { name: 'Portals Nous' },
  { name: 'Cas Català' },
  { name: "Costa d'en Blanes" },
  { name: 'Cala Vinyes' },
  { name: 'Cala Fornells' },
  { name: 'Palmanova' },
  { name: 'San Agustín' },
  { name: 'Illetes' },
  { name: 'Santa Ponça' },
  { name: 'Costa de la Calma' },
  { name: 'Paguera' },
  { name: 'Camp de Mar' },
  { name: 'Calvià' },
  { name: 'El Toro' },
  { name: 'Son Caliu' },
  { name: 'Portals Vells' },
  { name: 'Puerto Adriano' },
  { name: 'Andratx' },
  { name: 'Puerto de Andratx' },
  { name: 'Son Vida' },
];


const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_location: '',
    property_type: '',
    service_required: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.email.trim()) newErrors.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = true;
    }
    if (!formData.property_type) newErrors.property_type = true;
    if (!formData.service_required) newErrors.service_required = true;
    if (!formData.message.trim()) newErrors.message = true;
    else if (formData.message.trim().length < 10) {
      newErrors.message = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Your message has been sent successfully');
      setFormData({
        name: '',
        email: '',
        phone: '',
        property_location: '',
        property_type: '',
        service_required: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact | Balearic Home Solutions Mallorca</title>
        <meta name="description" content="Get in touch with Balearic Home Solutions for a private consultation about your Mallorca property management needs. Based in Calvià, Southwest Mallorca." />
        <link rel="canonical" href="https://balearichomesolutions.com/contact" />
      </Helmet>
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {t.contactPage.label}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {t.contactPage.title}
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed">
              {t.contactPage.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl text-navy mb-8">
                {t.contactPage.contactInfo}
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-navy mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-navy mb-1">{t.contactPage.location}</h3>
                    <p className="text-stone-600 text-sm">
                      Calvià · Mallorca<br />
                      Balearic Islands · Spain
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-navy mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-navy mb-1">{t.contactPage.email}</h3>
                    <a 
                      href="mailto:info@balearichomesolutions.com" 
                      className="text-stone-600 text-sm hover:text-navy transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@balearichomesolutions.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-navy mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-navy mb-1">{t.contactPage.phone}</h3>
                    <a 
                      href="tel:+34671377417" 
                      className="text-stone-600 text-sm hover:text-navy transition-colors"
                      data-testid="contact-phone-link"
                    >
                      +34 671 377 417
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-stone-50 border-l-2 border-navy">
                <h3 className="font-heading text-lg text-navy mb-2">
                  {t.contactPage.responseTime}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {t.contactPage.responseTimeText}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-stone-50 p-12 text-center"
                  data-testid="contact-success-message"
                >
                  <CheckCircle size={48} className="text-green-600 mx-auto mb-6" />
                  <h2 className="font-heading text-2xl text-navy mb-4">
                    {t.contactPage.successTitle}
                  </h2>
                  <p className="text-stone-600 leading-relaxed max-w-md mx-auto">
                    {t.contactPage.successMessage}
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="send-another-btn"
                    className="mt-8 text-navy underline underline-offset-4 text-sm font-medium hover:text-navy-light transition-colors"
                  >
                    {t.contactPage.sendAnother}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Full Name */}
                    <div>
                      <label className="form-label">
                        {t.contactPage.form.fullName} <span className="text-red-500">{t.contactPage.form.required}</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t.contactPage.form.fullName}
                        data-testid="contact-name-input"
                        className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="form-label">
                        {t.contactPage.form.emailLabel} <span className="text-red-500">{t.contactPage.form.required}</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                        className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="form-label">{t.contactPage.form.phoneLabel}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+34 ___ ___ ___"
                        data-testid="contact-phone-input"
                        className="form-input"
                      />
                    </div>

                    {/* Property Location */}
                    <div>
                      <label className="form-label">{t.contactPage.form.propertyLocation}</label>
                      <Select 
                        value={formData.property_location} 
                        onValueChange={(value) => handleSelectChange('property_location', value)}
                      >
                        <SelectTrigger 
                          data-testid="contact-location-select"
                          className="w-full bg-transparent border-0 border-b rounded-none px-0 py-4 h-auto text-base focus:ring-0 border-stone-300"
                        >
                          <SelectValue placeholder={t.contactPage.form.selectArea} />
                        </SelectTrigger>
                        <SelectContent>
                          {t.mallorcaAreas.map((area) => (
                            <SelectItem key={area} value={area}>
                              {area}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Property Type */}
                    <div>
                      <label className="form-label">
                        {t.contactPage.form.propertyType} <span className="text-red-500">{t.contactPage.form.required}</span>
                      </label>
                      <Select 
                        value={formData.property_type} 
                        onValueChange={(value) => handleSelectChange('property_type', value)}
                      >
                        <SelectTrigger 
                          data-testid="contact-property-type-select"
                          className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-4 h-auto text-base focus:ring-0 ${
                            errors.property_type ? 'border-red-500' : 'border-stone-300'
                          }`}
                        >
                          <SelectValue placeholder={t.contactPage.form.selectPropertyType} />
                        </SelectTrigger>
                        <SelectContent>
                          {t.propertyTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Service Required */}
                    <div>
                      <label className="form-label">
                        {t.contactPage.form.serviceRequired} <span className="text-red-500">{t.contactPage.form.required}</span>
                      </label>
                      <Select 
                        value={formData.service_required} 
                        onValueChange={(value) => handleSelectChange('service_required', value)}
                      >
                        <SelectTrigger 
                          data-testid="contact-service-select"
                          className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-4 h-auto text-base focus:ring-0 ${
                            errors.service_required ? 'border-red-500' : 'border-stone-300'
                          }`}
                        >
                          <SelectValue placeholder={t.contactPage.form.selectService} />
                        </SelectTrigger>
                        <SelectContent>
                          {t.serviceOptions.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="form-label">
                      {t.contactPage.form.message} <span className="text-red-500">{t.contactPage.form.required}</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t.contactPage.form.messagePlaceholder}
                      rows={5}
                      data-testid="contact-message-input"
                      className={`form-input resize-none ${errors.message ? 'border-red-500' : ''}`}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                      className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          {t.contactPage.form.sending}
                        </>
                      ) : (
                        <>
                          {t.contactPage.form.submit}
                          <Send size={16} />
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-stone-500">
                    {t.contactPage.form.privacyNote}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-navy mb-4">
              {t.contactPage.mapTitle}
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto mb-8">
              {t.contactPage.mapDesc}
            </p>
            <div className="bg-navy/10 h-64 flex items-center justify-center">
              <div className="text-center">
                <MapPin size={32} className="text-navy mx-auto mb-3" />
                <p className="text-navy font-medium">Calvià · Mallorca</p>
                <p className="text-stone-500 text-sm">Balearic Islands · Spain</p>
                <p className="text-navy font-medium mt-2">+34 671 377 417</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section data-testid="contact-areas-we-serve" className="py-12 md:py-16 bg-white border-t border-stone-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400 mb-6">
            {t.areasWeServe.label}
          </p>
          <p className="text-sm text-stone-500 leading-loose">
            {contactAreas.map((area, index) => (
              <span key={area.name}>
                {area.link ? (
                  <Link
                    to={area.link}
                    data-testid={`contact-area-link-${area.link.slice(1)}`}
                    className="text-navy underline underline-offset-4 decoration-stone-300 hover:decoration-navy transition-colors"
                  >
                    {area.name}
                  </Link>
                ) : (
                  area.name
                )}
                {index < contactAreas.length - 1 && (
                  <span className="text-stone-300 mx-2">·</span>
                )}
              </span>
            ))}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
